import { useEffect, useRef, useState } from "react";
import { Mic, Square, Trash2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import ysFixWebmDuration from "fix-webm-duration";
import api, { formatApiErrorDetail } from "@/lib/api";
import VoicePlayer from "./VoicePlayer";

/**
 * Reusable Voice Recorder.
 *
 * Props:
 *   value          - current voice URL (string | null) — when set, shows playback + Clear
 *   onChange(url, durationSec) - fires on upload-success or clear (url=null)
 *   uploadEndpoint - "/upload" (default) or "/delegation/attachments"
 *   uploadFields   - extra FormData fields, e.g. { purpose: "leave_voice" }
 *   urlFromResponse(data) - extractor; default builds `/api/files/${data.path}`
 *   maxSeconds     - hard cap (default 60)
 *   compact        - smaller layout for inline composer use
 *   testIdPrefix   - prefix for data-testids (default "voice")
 */
export default function VoiceRecorder({
  value,
  onChange,
  uploadEndpoint = "/upload",
  uploadFields = { purpose: "voice_note" },
  urlFromResponse,
  maxSeconds = 60,
  compact = false,
  testIdPrefix = "voice",
  disabled = false,
}) {
  const [state, setState] = useState("idle"); // idle | recording | uploading | recorded
  const [seconds, setSeconds] = useState(0);
  const [localUrl, setLocalUrl] = useState(null);
  const mediaRecorderRef = useRef(null);
  const timerRef = useRef(null);
  const streamRef = useRef(null);
  // Use ref to hold latest seconds (avoid stale closure in mr.onstop)
  const secondsRef = useRef(0);

  // Cleanup on unmount
  useEffect(() => () => {
    if (timerRef.current) clearInterval(timerRef.current);
    if (localUrl) URL.revokeObjectURL(localUrl);
    streamRef.current?.getTracks().forEach((t) => t.stop());
  }, [localUrl]);

  const fmt = (s) => `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;

  const start = async () => {
    if (disabled || state !== "idle") return;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const mime = MediaRecorder.isTypeSupported("audio/webm") ? "audio/webm" : "";
      const mr = mime ? new MediaRecorder(stream, { mimeType: mime }) : new MediaRecorder(stream);
      const chunks = [];
      const startedAt = Date.now();
      mr.ondataavailable = (ev) => { if (ev.data?.size > 0) chunks.push(ev.data); };
      mr.onstop = async () => {
        const finalSeconds = secondsRef.current;
        const durationMs = Date.now() - startedAt;
        stream.getTracks().forEach((t) => t.stop());
        streamRef.current = null;
        if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
        const rawBlob = new Blob(chunks, { type: mime || "audio/webm" });
        // Patch WebM duration metadata so <audio controls> shows real duration
        // (MediaRecorder otherwise omits it, causing 00:00 / Infinity display).
        let blob = rawBlob;
        if ((mime || "audio/webm").includes("webm")) {
          try {
            blob = await ysFixWebmDuration(rawBlob, durationMs, { logger: false });
          } catch (fixErr) {
            // Non-fatal: fall back to raw blob; UI will show fallback duration label.
            blob = rawBlob;
          }
        }
        const blobUrl = URL.createObjectURL(blob);
        setLocalUrl(blobUrl);
        setState("uploading");
        try {
          const fd = new FormData();
          const ext = (mime || "audio/webm").includes("webm") ? "webm" : "ogg";
          fd.append("file", blob, `voice.${ext}`);
          Object.entries(uploadFields || {}).forEach(([k, v]) => fd.append(k, v));
          const { data } = await api.post(uploadEndpoint, fd, {
            headers: { "Content-Type": "multipart/form-data" },
          });
          const url = urlFromResponse ? urlFromResponse(data) : `/api/files/${data.path}`;
          onChange?.(url, finalSeconds, data);
          setState("recorded");
          toast.success("Voice note attached");
        } catch (e) {
          toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Upload failed");
          setState("idle");
          URL.revokeObjectURL(blobUrl);
          setLocalUrl(null);
        }
      };
      mediaRecorderRef.current = mr;
      mr.start();
      setState("recording");
      setSeconds(0);
      secondsRef.current = 0;
      timerRef.current = setInterval(() => {
        secondsRef.current += 1;
        setSeconds(secondsRef.current);
        if (secondsRef.current >= maxSeconds) {
          try { mr.state === "recording" && mr.stop(); } catch { /* ignore */ }
        }
      }, 1000);
    } catch (e) {
      toast.error("Microphone access denied or unavailable");
      setState("idle");
    }
  };

  // Use ref to hold latest seconds (avoid stale closure in mr.onstop)

  const stop = () => {
    try {
      mediaRecorderRef.current?.state === "recording" && mediaRecorderRef.current.stop();
    } catch { /* ignore */ }
  };

  const clear = () => {
    if (localUrl) URL.revokeObjectURL(localUrl);
    setLocalUrl(null);
    setSeconds(0);
    secondsRef.current = 0;
    setState("idle");
    onChange?.(null, 0, null);
  };

  // When parent clears `value`, mirror state
  useEffect(() => {
    if (!value && state === "recorded") {
      if (localUrl) URL.revokeObjectURL(localUrl);
      setLocalUrl(null);
      setSeconds(0);
      secondsRef.current = 0;
      setState("idle");
    }
  }, [value]); // eslint-disable-line

  const hasRecording = !!value || state === "recorded";
  const playSrc = localUrl || (value ? value : null);

  return (
    <div
      className={`flex items-center gap-2 ${compact ? "" : "p-3 bg-red-50/40 border border-red-200 rounded"}`}
      data-testid={`${testIdPrefix}-recorder`}
    >
      {state === "idle" && !hasRecording && (
        <button
          type="button"
          onClick={start}
          disabled={disabled}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium border border-red-200 rounded bg-white hover:bg-red-50 brand-text disabled:opacity-50"
          data-testid={`${testIdPrefix}-record-btn`}
        >
          <Mic className="h-3.5 w-3.5" /> Record voice ({maxSeconds}s max)
        </button>
      )}

      {state === "recording" && (
        <>
          <span
            className="inline-flex items-center gap-1.5 text-xs font-medium text-red-700"
            data-testid={`${testIdPrefix}-recording-indicator`}
          >
            <span className="h-2 w-2 rounded-full bg-red-500 animate-pulse" />
            REC <span className="font-mono" data-testid={`${testIdPrefix}-timer`}>{fmt(seconds)}</span>
          </span>
          <button
            type="button"
            onClick={stop}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium border border-red-200 rounded bg-red-50 text-red-700 hover:bg-red-100"
            data-testid={`${testIdPrefix}-stop-btn`}
          >
            <Square className="h-3.5 w-3.5" /> Stop
          </button>
        </>
      )}

      {state === "uploading" && (
        <span className="inline-flex items-center gap-1.5 text-xs text-slate-600" data-testid={`${testIdPrefix}-uploading`}>
          <Loader2 className="h-3.5 w-3.5 animate-spin" /> Uploading…
        </span>
      )}

      {hasRecording && state !== "recording" && state !== "uploading" && playSrc && (
        <>
          <VoicePlayer
            src={playSrc}
            className="h-8 max-w-[260px]"
            data-testid={`${testIdPrefix}-preview`}
          />
          <button
            type="button"
            onClick={clear}
            className="inline-flex items-center gap-1 text-xs text-red-700 hover:underline"
            data-testid={`${testIdPrefix}-clear-btn`}
          >
            <Trash2 className="h-3 w-3" /> Clear
          </button>
        </>
      )}
    </div>
  );
}
